import { useMemo, useCallback, useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Text,
  Table,
  Button,
  Flex,
  Badge,
  Grid,
  Separator,
} from "@radix-ui/themes";
import {
  ArrowLeftIcon,
  CheckIcon,
  Cross1Icon,
  InfoCircledIcon,
} from "@radix-ui/react-icons";
import { PageLayoutV2 } from "@/v2/components/layout/page-layout/PageLayout";
import { PageHeader } from "@/v2/components/layout";
import { Section } from "@radix-ui/themes";
import { useReleases } from "@/routes/simulator/context/release";
import { SimulatorChannelIcon } from "@/routes/simulators/SimulatorChannelIcon";
import styles from "./TestRunDetails.module.scss";
import { useRegressionApi } from "@/v2/entities/regression/Regression.api";
import { useQuery } from "react-query";
import { RatioFormatter } from "@/v2/components/formatters/RationFormatter";
import { ExpandableText } from "@/v2/components/expandableText/ExpandableText";
import { CopyPasteCell } from "@/routes/explore/CellGenerator";
import { RegressionBreadcrumbs } from "./RegressionBreadcrumbs";
import { StructureCheck } from "@/v2/assets/icons/StructureCheck";

export function TestRunDetails() {
  const { agentId, testId, setId } = useParams();
  const navigate = useNavigate();
  const { simulators } = useReleases();
  const { getRegressionTestInfo, getRegressionTestResults, getRegressionSet } =
    useRegressionApi();
  const { data: testInfo } = useQuery({
    queryKey: ["regression-test-info", testId],
    queryFn: () => getRegressionTestInfo(testId || ""),
  });
  const { data: questions } = useQuery({
    queryKey: ["regression-test-results", testId],
    queryFn: () => getRegressionTestResults(testId || ""),
  });
  const { data: setInfo } = useQuery({
    queryKey: ["regression-set", setId],
    queryFn: () => getRegressionSet(setId || ""),
  });
  const [selectedQuestion, setSelectedQuestion] = useState<string | null>(null);

  useEffect(() => {
    if (questions?.length && questions.length > 0 && !selectedQuestion) {
      setSelectedQuestion(questions[0].id);
    }
  }, [questions, selectedQuestion]);

  const agent = useMemo(() => {
    return simulators?.find((s: any) => s.id.toString() === agentId);
  }, [simulators, agentId]);

  const formatDate = useCallback((dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }, []);

  const selectedQuestionData = useMemo(() => {
    if (!selectedQuestion) return null;
    return questions?.find((q: any) => q.id === selectedQuestion);
  }, [selectedQuestion, questions]);

  const getResultColor = useCallback((result: string) => {
    return result === "pass" ? "green" : "red";
  }, []);

  const getSimilarityColor = useCallback((similarity: number) => {
    if (similarity >= 0.8) return "green";
    if (similarity >= 0.6) return "yellow";
    return "red";
  }, []);

  if (!agent) {
    return (
      <PageLayoutV2>
        <Flex justify="center" align="center" style={{ minHeight: "400px" }}>
          <Text size="3" color="gray">
            Agent not found
          </Text>
        </Flex>
      </PageLayoutV2>
    );
  }

  return (
    <PageLayoutV2>
      <Flex direction="column">
        <RegressionBreadcrumbs
          agentId={agentId}
          agentName={agent.name}
          agentChannel={agent.channel}
          regressionSetName={setInfo?.name}
          regressionSetId={setId}
          testRunId={testId}
        />
        <PageHeader
          title={`Test Run: ${testId}`}
          description={`${setInfo?.name} - "${agent.name}" agent`}
          icon={<StructureCheck size={32} fill="gray" />}
        />

        <Section size="4" pt="0">
          <Flex direction="column" width="100%">
            {/* Test Run Summary - With border bottom */}
            <Flex
              direction="row"
              gap="6"
              align="center"
              p="4"
              style={{
                borderBottom: "1px solid var(--gray-4)",
              }}
            >
              <Flex direction="column" gap="1">
                <Text size="2" color="gray">
                  Accuracy Rate
                </Text>
                <Text size="4" weight="medium">
                  {RatioFormatter({
                    value: testInfo?.avgAccuracy,
                  })}
                </Text>
              </Flex>
              <Flex direction="column" gap="1">
                <Text size="2" color="gray">
                  Total Questions
                </Text>
                <Text size="4" weight="medium">
                  {testInfo?.numberOfResults}
                </Text>
              </Flex>
              <Flex direction="column" gap="1">
                <Text size="2" color="gray">
                  Passed
                </Text>
                <Badge size="2" color="green">
                  {testInfo?.passedQuestions}
                </Badge>
              </Flex>
              <Flex direction="column" gap="1">
                <Text size="2" color="gray">
                  Failed
                </Text>
                <Badge size="2" color="red">
                  {testInfo?.failedQuestions}
                </Badge>
              </Flex>
              <Flex direction="column" gap="1">
                <Text size="2" color="gray">
                  Run Date
                </Text>
                <Text size="2">
                  {testInfo?.createdAt
                    ? formatDate(testInfo.createdAt)
                    : "Unknown"}
                </Text>
              </Flex>
            </Flex>

            {/* Two Panel Layout */}
            <Box
              style={{
                height: "calc(100vh - 300px)",
                minHeight: "600px",
              }}
            >
              <Grid columns="2" width="100%" style={{ height: "100%" }}>
                {/* Left Panel - Questions */}
                <Box
                  style={{
                    height: "100%",
                    borderRadius: 0,
                    padding: "16px",
                    backgroundColor: "var(--color-surface)",
                    borderRight: "1px solid var(--gray-4)",
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                  }}
                >
                  <Flex
                    direction="column"
                    gap="3"
                    style={{ marginBottom: "16px", flexShrink: 0 }}
                  >
                    <Flex direction={"row"} gap={"2"} align="center">
                      <Flex gap={"2"} align={"center"}>
                        <Text size={"3"} weight={"medium"}>
                          Questions
                        </Text>
                        <Badge color="gray">{questions?.length ?? 0}</Badge>
                      </Flex>
                    </Flex>
                  </Flex>
                  <Box
                    style={{ flex: 1, overflowY: "auto", overflowX: "hidden" }}
                  >
                    <Table.Root>
                      <Table.Header>
                        <Table.Row className=" text-gray-500 font-normal">
                          <Table.ColumnHeaderCell className=" font-normal">
                            Question ID
                          </Table.ColumnHeaderCell>
                          <Table.ColumnHeaderCell className=" font-normal">
                            Result
                          </Table.ColumnHeaderCell>
                          <Table.ColumnHeaderCell className=" font-normal">
                            Similarity
                          </Table.ColumnHeaderCell>
                          <Table.ColumnHeaderCell className=" font-normal">
                            Question
                          </Table.ColumnHeaderCell>
                        </Table.Row>
                      </Table.Header>
                      <Table.Body>
                        {questions?.map((q) => (
                          <Table.Row
                            key={q.id}
                            className={`${styles.questionRow} ${
                              selectedQuestion === q.id
                                ? styles.selectedQuestion
                                : ""
                            }`}
                            onClick={() => setSelectedQuestion(q.id)}
                          >
                            <Table.Cell>
                              <CopyPasteCell value={q.id} truncate />
                            </Table.Cell>
                            <Table.Cell>
                              <Badge
                                size="2"
                                color={getResultColor(
                                  q.similarity > 0.8 ? "pass" : "fail"
                                )}
                              >
                                {q.similarity > 0.8 ? (
                                  <CheckIcon />
                                ) : (
                                  <Cross1Icon />
                                )}
                                {q.similarity > 0.8 ? "Pass" : "Fail"}
                              </Badge>
                            </Table.Cell>
                            <Table.Cell>
                              <Badge
                                size="2"
                                color={getSimilarityColor(q.similarity)}
                              >
                                {(q.similarity * 100).toFixed(0)}%
                              </Badge>
                            </Table.Cell>

                            <Table.Cell>
                              <ExpandableText
                                text={q.regressionSetItem?.question}
                              />
                            </Table.Cell>
                          </Table.Row>
                        ))}
                      </Table.Body>
                    </Table.Root>
                  </Box>
                </Box>

                {/* Right Panel - Question Details */}
                <Box
                  style={{
                    height: "100%",
                    borderRadius: 0,
                    padding: "16px",
                    backgroundColor: "var(--color-surface)",
                    borderBottom: "1px solid var(--gray-4)",
                  }}
                >
                  {selectedQuestionData ? (
                    <Flex direction="column" gap="4" p="4">
                      {/* Similarity and Question on top */}
                      <Flex direction="column" gap="3">
                        <Flex justify="between" align="center">
                          <Badge
                            size="2"
                            color={getSimilarityColor(
                              selectedQuestionData.similarity
                            )}
                          >
                            {(selectedQuestionData.similarity * 100).toFixed(0)}
                            %
                          </Badge>
                        </Flex>
                        <Text size="3" weight="medium">
                          {selectedQuestionData.regressionSetItem?.question}
                        </Text>
                      </Flex>

                      <Separator />

                      {/* Question ID without label */}
                      <Text size="2" weight="medium">
                        {selectedQuestionData.id}
                      </Text>

                      <Separator />

                      {/* Expected Answer */}
                      <Flex direction="column" gap="2">
                        <Text size="2" color="gray">
                          Expected Answer
                        </Text>
                        <Text size="2">
                          {
                            selectedQuestionData.regressionSetItem
                              ?.expectedAnswer
                          }
                        </Text>
                      </Flex>

                      <Separator />

                      {/* AI Response */}
                      <Flex direction="column" gap="2">
                        <Text size="2" color="gray">
                          AI Response
                        </Text>
                        <Text size="2">{selectedQuestionData.aiResponse}</Text>
                      </Flex>
                    </Flex>
                  ) : (
                    <Flex
                      justify="center"
                      align="center"
                      style={{ minHeight: "200px" }}
                    >
                      <Flex direction="column" gap="2" align="center">
                        <InfoCircledIcon />
                        <Text size="2" color="gray">
                          Select a question to view details
                        </Text>
                      </Flex>
                    </Flex>
                  )}
                </Box>
              </Grid>
            </Box>
          </Flex>
        </Section>
      </Flex>
    </PageLayoutV2>
  );
}
